import { Divider, Grid, Typography, useTheme } from "@mui/material";
import { addDays, isAfter, isBefore } from "date-fns";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Page } from "src/models/api/page";
import { Sort } from "src/models/api/sort";
import { v4 as uuidv4 } from "uuid";
import AllEmployeeLeavesCalendar from "./AllEmployeeLeavesCalendar";
import { SPACING } from "../../../../_globalStore/redux/reducers/Theme/themeConstants";
import FilterByYearAndMonth from "../../../../containers/FilterByYearAndMonth/FilterByYearAndMonth";
import useChangeYearAndMonth from "../../../../containers/FilterByYearAndMonth/useChangeYearAndMonth/useChangeYearAndMonth";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import {
  AbsencesStatusesQuickfilter,
  useAbsencesStatusesQuickFilter,
} from "../../../../containers/Quickfilters/AbsencesStatusesQuickfilter";
import TableHeader from "../../../../containers/TableHeader/TableHeader";
import useAuthorization from "../../../../custom-hooks/useAuthorization/useAuthorization";
import useSearchOptions from "../../../../custom-hooks/useSearchOptions/useSearchOptions";
import useWindowSize from "../../../../custom-hooks/useWindowSize/useWindowSize";
import { CustomDate } from "../../../../models/custom-date/custom-date";
import { EmployeeDTO } from "../../../../models/employees";
import { EmployeesFilter } from "../../../../models/employees/employees-filter";
import { TnAAbsenceDTO } from "../../../../models/tna/tna-absences/tna-absence-dto";
import { TnAAbsencesFilter } from "../../../../models/tna/tna-absences/tna-absences-filter";
// import { TnACounterDTO } from "../../../../models/tna/tna-counters/tna-counter-dto";
import { TnAEmployeeActualConfigDTO } from "../../../../models/tna/tna-employee-config/tna-employee-config-dto";
// import { TnAMobileHoursBalanceDTO } from "../../../../models/tna/tna-mobile-hours/tna-mobile-hours-balance-dto";
import AutocompleteDropdown, {
  allAutocompliteOption,
} from "../../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { NotAuthorizedPage } from "../../../../reusable-components/NotAuthorizedPage/NotAuthorizedPage";
import { EmployeesApiService } from "../../../../services/employees/employees-api.service";
import { TnAAbsencesApiService } from "../../../../services/tna-absences/tna-absences-api.service";
// import { loadTnACounters, loadTnAMobileHours } from "../../../../services/tna-clockings/tna-data-api.service";
import { loadEETnAActualConfig } from "../../../../services/tna-config/tna-employee-config-api.service";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import { FT_MLT } from "../../../../utils/constants/filter-tables-constants";
import { toastifyResponse } from "../../../../utils/toastify-response/toastify-response";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
// import { AbsencesPeriodQuickfilter } from "../AbsencesPeriodQuickfilter/AbsencesPeriodQuickfilter";
// import { useAbsencesPeriodQuickFilter } from "../AbsencesPeriodQuickfilter/useAbsencesPeriodQuickFilter";
// import ExportAllEmployeeLeavesToExcel from "../ExportAllEmployeeLeavesToExcel/ExportAllEmployeeLeavesToExcel";

export interface State {
  allEmployees: boolean;
  employee: EmployeeDTO | null;
  loading: boolean;
  tnAEmployeeActualConfig: TnAEmployeeActualConfigDTO | null;
  // counters: TnACounterDTO[];
  // mobileHours: TnAMobileHoursBalanceDTO[];
  error: string;
  items: TnAAbsenceDTO[];
  totalItems: number;
  page: Page;
  sort: Sort[];

  employeeQfId: string; // rerender autocomplete to set "All" option as default
}

const AllEmployeeLeavesCalendarWrapper = () => {
  const hasAccess = useAuthorization([
    AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves,
    AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData,
  ]);

  const { height } = useWindowSize();

  const { searchOptions, handleChangeSearchOptions } = useSearchOptions(FT_MLT);

  const {
    legalEntity: { legalEntityOption, legalEntityQuickFilter },
    employeeGroup: { employeeGroupOption, employeeGroupQuickFilter },
  } = useContext(MyTnAContext);

  // const { absencesPeriodQuickFilter, setAbsencesPeriodQuickFilter } = useAbsencesPeriodQuickFilter();

  const { absencesStatusesQuickFilter, setAbsencesStatusesQuickFilter } = useAbsencesStatusesQuickFilter();

  const { year, month, handleChangeYearAndMonth } = useChangeYearAndMonth();

  const absencesStatuses = useMemo(() => {
    return absencesStatusesQuickFilter.absencesStatuses.absenceStatuses.length
      ? absencesStatusesQuickFilter.absencesStatuses.absenceStatuses.reduce((acc, curr) => {
          return {
            ...acc,
            ...curr,
          };
        }, {})
      : {};
  }, [absencesStatusesQuickFilter]);

  const theme = useTheme();

  const [state, setState] = useState<State>({
    allEmployees: true,
    employee: null,
    loading: true,
    tnAEmployeeActualConfig: null,
    // counters: [],
    // mobileHours: [],
    error: "",
    items: [],
    totalItems: 0,
    page: {
      pageNumber: 0,
      pageSize: 10000,
    },
    sort: [
      {
        name: new TnAAbsencesApiService().defaultSort[0]?.name ?? "",
        direction: new TnAAbsencesApiService().defaultSort[0]?.direction ?? 0,
      },
    ],
    employeeQfId: uuidv4(),
  });

  const dateWithoutTime = (date: Date | null) => {
    const year = date ? new Date(date).getUTCFullYear() : null;
    const month = date
      ? `${
          new Date(date).getUTCMonth() + 1 < 10
            ? `0${new Date(date).getUTCMonth() + 1}`
            : new Date(date).getUTCMonth() + 1
        }`
      : null;
    const day = date
      ? `${new Date(date).getUTCDate() < 10 ? `0${new Date(date).getUTCDate()}` : new Date(date).getUTCDate()}`
      : null;

    return !date ? null : `${year}-${month}-${day}`;
  };

  const minDate = useMemo(() => {
    const tsStartDate = state.tnAEmployeeActualConfig?.timesheetTrackingStartDate
      ? new Date(state.tnAEmployeeActualConfig.timesheetTrackingStartDate)
      : new Date(2099, 11, 31);
    const ctStartDate = state.tnAEmployeeActualConfig?.contractStartDate
      ? new Date(state.tnAEmployeeActualConfig.contractStartDate)
      : new Date(2099, 11, 31);
    const calendarStartDate = isAfter(ctStartDate, tsStartDate) ? ctStartDate : tsStartDate;

    const lastSignedOffDate = state.tnAEmployeeActualConfig?.lastSignOffRequestAsOfDate
      ? !!state.tnAEmployeeActualConfig?.lastSignedOffDate &&
        isBefore(
          new Date(state.tnAEmployeeActualConfig.lastSignOffRequestAsOfDate),
          new Date(state.tnAEmployeeActualConfig.lastSignedOffDate)
        )
        ? new Date(state.tnAEmployeeActualConfig.lastSignedOffDate)
        : new Date(state.tnAEmployeeActualConfig.lastSignOffRequestAsOfDate)
      : state.tnAEmployeeActualConfig?.lastSignedOffDate
      ? new Date(state.tnAEmployeeActualConfig.lastSignedOffDate)
      : new Date(0, 0, 0);

    const canSignOffSince = addDays(lastSignedOffDate, 1);

    const calendarMinDate = isAfter(canSignOffSince, calendarStartDate) ? canSignOffSince : calendarStartDate;

    return calendarMinDate;
  }, [state.tnAEmployeeActualConfig]);

  const maxDate = useMemo(() => {
    const tsEndDate = state.tnAEmployeeActualConfig?.timesheetTrackingEndDate
      ? new Date(state.tnAEmployeeActualConfig.timesheetTrackingEndDate)
      : new Date(2099, 11, 31);

    const ctEndDate = state.tnAEmployeeActualConfig?.contractEndDate
      ? new Date(state.tnAEmployeeActualConfig.contractEndDate)
      : new Date(2099, 11, 31);

    const calendarEndDate = isBefore(ctEndDate, tsEndDate) ? ctEndDate : tsEndDate;

    return calendarEndDate;
  }, [state.tnAEmployeeActualConfig]);

  const loadEmployeeConfigCallback = useCallback(async () => {
    if (state.employee?.id) {
      const atDate = CustomDate.NewCustomDate(new Date().getFullYear(), new Date().getMonth() + 1, 1);
      const response = await loadEETnAActualConfig(state.employee.id, atDate, undefined);

      toastifyResponse(response, "failed");

      return response;
    }
  }, [state.employee?.id]);

  // const loadVacationCountersCallback = useCallback(async () => {
  //   const year = new Date().getFullYear();
  //   const month = new Date().getMonth();

  //   if (state.employee?.id) {
  //     const start = new Date(year, month, 1);
  //     const end = addDays(addMonths(start, 1), -1);

  //     const response = await loadTnACounters(
  //       {
  //         employeeId: state.employee.id,
  //         years: [start.getFullYear(), end.getFullYear()],
  //       },
  //       false,
  //       undefined
  //     );

  //     toastifyResponse(response, "failed");

  //     return response;
  //   }
  // }, [state.employee?.id]);

  // const loadMobileHoursCallback = useCallback(async () => {
  //   if (state.employee?.id) {
  //     const response = await loadTnAMobileHours(
  //       { employeeId: state.employee.id, year: new Date().getFullYear(), month: new Date().getMonth() },
  //       undefined
  //     );

  //     toastifyResponse(response, "failed");

  //     return response;
  //   }
  // }, [state.employee?.id]);

  const loadEmployeeAbsences = useCallback(
    async (signal?: AbortSignal) => {
      if (state.employee?.id && !state.allEmployees) {
        const response = await new TnAAbsencesApiService().loadItems(
          {
            employeeIds: [state.employee.id],
            ...absencesStatuses,
            includeSignedOff: true,
            // fromDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.fromDate),
            // toDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.toDate),
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
          } as TnAAbsencesFilter,
          searchOptions.query,
          state.sort,
          state.page,
          searchOptions.exactMatch,
          60,
          signal
        );

        toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");

        return response;
      } else if (state.allEmployees) {
        const response = await new TnAAbsencesApiService().loadItems(
          {
            employeeIds: [] as string[],
            ...absencesStatuses,
            includeSignedOff: true,
            // fromDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.fromDate),
            // toDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.toDate),
            employeeGroupIds: employeeGroupOption?.employeeGroupIds,
            legalEntityIds: legalEntityOption?.legalEntityIds,
          } as TnAAbsencesFilter,
          searchOptions.query,
          state.sort,
          state.page,
          searchOptions.exactMatch,
          60,
          signal
        );

        toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");

        return response;
      }
    },
    [
      searchOptions,
      state.page,
      state.employee?.id,
      state.allEmployees,
      // absencesPeriodQuickFilter.absencesPeriod,
      absencesStatuses,
      state.sort,
      employeeGroupOption?.employeeGroupIds,
      legalEntityOption?.legalEntityIds,
      year,
      month,
    ]
  );

  const loadDataForEmployee = useCallback(async () => {
    if (state.employee?.id && !state.allEmployees) {
      setState((state) => ({
        ...state,
        loading: true,
        error: "",
      }));

      try {
        const employeeConfigResponse = await loadEmployeeConfigCallback();

        if (employeeConfigResponse?.error)
          return setState((state) => ({ ...state, error: employeeConfigResponse.error }));

        if (!employeeConfigResponse?.items[0])
          return setState((state) => ({ ...state, tnAEmployeeActualConfig: null }));

        if (employeeConfigResponse?.items[0]) {
          // const [emplyeeAbsencesResponse, vacationCountersResponse, mobileHoursResponse] = await Promise.all([
          //   loadEmployeeAbsences(),
          //   loadVacationCountersCallback(),
          //   loadMobileHoursCallback(),
          // ]);
          const emplyeeAbsencesResponse = await loadEmployeeAbsences();

          // const error = emplyeeAbsencesResponse?.error || vacationCountersResponse?.error || mobileHoursResponse?.error;
          const error = emplyeeAbsencesResponse?.error;

          if (error) {
            return setState((state) => ({ ...state, error }));
          }

          setState((state) => ({
            ...state,
            tnAEmployeeActualConfig: employeeConfigResponse.items[0],
            items: emplyeeAbsencesResponse?.items ?? [],
            totalItems: emplyeeAbsencesResponse?.totalItems ?? 0,
            // counters: vacationCountersResponse?.items ?? [],
            // mobileHours: mobileHoursResponse?.items ?? [],
            error: "",
          }));
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    } else if (state.allEmployees) {
      setState((state) => ({
        ...state,
        loading: true,
        error: "",
      }));

      try {
        const emplyeeAbsencesResponse = await loadEmployeeAbsences();

        if (emplyeeAbsencesResponse?.error)
          return setState((state) => ({ ...state, error: emplyeeAbsencesResponse.error }));

        setState((state) => ({
          ...state,
          items: emplyeeAbsencesResponse?.items ?? [],
          totalItems: emplyeeAbsencesResponse?.totalItems ?? 0,
          error: "",
        }));
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    }
  }, [
    loadEmployeeConfigCallback,
    loadEmployeeAbsences,
    // loadVacationCountersCallback,
    // loadMobileHoursCallback,
    state.employee?.id,
    state.allEmployees,
  ]);

  const notifyParent = useCallback(async () => {
    if (state.employee?.id && !state.allEmployees) {
      setState((state) => ({
        ...state,
        // counters: [],
        loading: true,
        error: "",
      }));

      try {
        const emplyeeAbsencesResponse = await loadEmployeeAbsences();

        if (emplyeeAbsencesResponse?.error)
          return setState((state) => ({ ...state, error: emplyeeAbsencesResponse.error }));

        // const vacationCountersResponse = await loadVacationCountersCallback();

        // if (vacationCountersResponse?.error)
        //   return setState((state) => ({ ...state, error: vacationCountersResponse.error }));

        setState((state) => ({
          ...state,
          items: emplyeeAbsencesResponse?.items ?? [],
          totalItems: emplyeeAbsencesResponse?.totalItems ?? 0,
          // counters: vacationCountersResponse?.items ?? [],
          error: "",
        }));
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    } else if (state.allEmployees) {
      setState((state) => ({
        ...state,
        // counters: [],
        loading: true,
        error: "",
      }));

      try {
        const emplyeeAbsencesResponse = await loadEmployeeAbsences();

        if (emplyeeAbsencesResponse?.error)
          return setState((state) => ({ ...state, error: emplyeeAbsencesResponse.error }));

        setState((state) => ({
          ...state,
          items: emplyeeAbsencesResponse?.items ?? [],
          totalItems: emplyeeAbsencesResponse?.totalItems ?? 0,
          error: "",
        }));
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({
          ...state,
          loading: false,
        }));
      }
    }
  }, [loadEmployeeAbsences, /*loadVacationCountersCallback,*/ state.employee?.id, state.allEmployees]);

  const loadAbsences = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (state.employee?.id && !state.allEmployees) {
        setState((state) => ({
          ...state,
          loading: true,
        }));

        try {
          const response = await new TnAAbsencesApiService().loadItems(
            {
              employeeIds: [state.employee.id],
              ...absencesStatuses,
              includeSignedOff: true,
              // fromDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.fromDate),
              // toDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.toDate),
              employeeGroupIds: employeeGroupOption?.employeeGroupIds,
              legalEntityIds: legalEntityOption?.legalEntityIds,
            } as TnAAbsencesFilter,
            searchOptions.query,
            state.sort,
            state.page,
            searchOptions.exactMatch,
            60,
            signal
          );
          if (!response.error) {
            setState((state) => ({
              ...state,
              items: response.items,
              totalItems: response.totalItems,
              loading: false,
              error: "",
            }));
          } else {
            setState((state) => ({
              ...state,
              items: [],
              totalItems: 0,
              loading: false,
              error: response.error,
            }));
          }

          toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");
        } catch (error) {
          setState((state) => ({
            ...state,
            loading: false,
            error: error as string,
          }));
        }
      } else if (state.allEmployees) {
        setState((state) => ({
          ...state,
          loading: true,
        }));

        try {
          const response = await new TnAAbsencesApiService().loadItems(
            {
              employeeIds: [] as string[],
              ...absencesStatuses,
              includeSignedOff: true,
              // fromDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.fromDate),
              // toDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.toDate),
              employeeGroupIds: employeeGroupOption?.employeeGroupIds,
              legalEntityIds: legalEntityOption?.legalEntityIds,
            } as TnAAbsencesFilter,
            searchOptions.query,
            state.sort,
            state.page,
            searchOptions.exactMatch,
            60,
            signal
          );
          if (!response.error) {
            setState((state) => ({
              ...state,
              items: response.items,
              totalItems: response.totalItems,
              loading: false,
              error: "",
            }));
          } else {
            setState((state) => ({
              ...state,
              items: [],
              totalItems: 0,
              loading: false,
              error: response.error,
            }));
          }

          toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");
        } catch (error) {
          setState((state) => ({
            ...state,
            loading: false,
            error: error as string,
          }));
        }
      }
    },
    [
      searchOptions,
      state.page,
      state.employee?.id,
      // absencesPeriodQuickFilter.absencesPeriod,
      absencesStatuses,
      state.sort,
      employeeGroupOption?.employeeGroupIds,
      legalEntityOption?.legalEntityIds,
    ]
  );

  // const loadAllAbsences = useCallback(
  //   async (signal?: AbortSignal) => {
  //     if (state.employee?.id && !state.allEmployees) {
  //       try {
  //         const response = await new TnAAbsencesApiService().loadItems(
  //           {
  //             employeeIds: [state.employee.id],
  //             ...absencesStatuses,
  //             includeSignedOff: true,
  //             // fromDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.fromDate),
  //             // toDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.toDate),
  //             employeeGroupIds: employeeGroupOption?.employeeGroupIds,
  //             legalEntityIds: legalEntityOption?.legalEntityIds,
  //           } as TnAAbsencesFilter,
  //           searchOptions.query,
  //           state.sort,
  //           state.page,
  //           searchOptions.exactMatch,
  //           60,
  //           signal
  //         );
  //         if (!response.error) {
  //           return response;
  //         }

  //         toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");
  //       } catch (error) {
  //         setState((state) => ({
  //           ...state,
  //           loading: false,
  //           error: error as string,
  //         }));
  //       }
  //     } else if (state.allEmployees) {
  //       try {
  //         const response = await new TnAAbsencesApiService().loadItems(
  //           {
  //             employeeIds: [] as string[],
  //             ...absencesStatuses,
  //             includeSignedOff: true,
  //             // fromDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.fromDate),
  //             // toDate: dateWithoutTime(absencesPeriodQuickFilter.absencesPeriod.toDate),
  //             employeeGroupIds: employeeGroupOption?.employeeGroupIds,
  //             legalEntityIds: legalEntityOption?.legalEntityIds,
  //           } as TnAAbsencesFilter,
  //           searchOptions.query,
  //           state.sort,
  //           state.page,
  //           searchOptions.exactMatch,
  //           60,
  //           signal
  //         );
  //         if (!response.error) {
  //           return response;
  //         }

  //         toastifyResponse(response, "failed", undefined, "failure_fetching_data", "error_fetching_data");
  //       } catch (error) {
  //         setState((state) => ({
  //           ...state,
  //           loading: false,
  //           error: error as string,
  //         }));
  //       }
  //     }
  //   },
  //   [
  //     searchOptions,
  //     state.page,
  //     state.employee?.id,
  //     // absencesPeriodQuickFilter.absencesPeriod,
  //     state.totalItems,
  //     absencesStatuses,
  //     state.sort,
  //     employeeGroupOption?.employeeGroupIds,
  //     legalEntityOption?.legalEntityIds,
  //     year,
  //     month,
  //   ]
  // );

  useEffect(() => {
    hasAccess &&
      ((state.employee?.id && !state.allEmployees) || (!state.employee?.id && state.allEmployees)) &&
      loadDataForEmployee();
  }, [state.employee?.id, hasAccess, state.allEmployees, year, month]);

  const currHeight = useRef<number>(
    height -
      (SPACING[9] * 2 +
        (document.getElementById("header")?.clientHeight ?? 0) +
        SPACING[7] +
        (document.getElementById("MultitabsHorizontal-Portal")?.clientHeight ?? 0) +
        SPACING[7] +
        (document.getElementById("tableHeader")?.clientHeight ?? 0) +
        SPACING[7] +
        // (document.getElementById("counters")?.clientHeight ?? 0) +
        // SPACING[7] +
        SPACING[4] +
        20)
  );

  useEffect(() => {
    currHeight.current =
      height -
      (SPACING[9] * 2 +
        (document.getElementById("header")?.clientHeight ?? 0) +
        SPACING[7] +
        (document.getElementById("MultitabsHorizontal-Portal")?.clientHeight ?? 0) +
        SPACING[7] +
        (document.getElementById("tableHeader")?.clientHeight ?? 0) +
        SPACING[7] +
        // (document.getElementById("counters")?.clientHeight ?? 0) +
        // SPACING[7] +
        SPACING[4]);
  }, [state.allEmployees, height /*, state.counters*/]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    searchOptions.query.length === 0
      ? loadAbsences(abortController.signal)
      : (timer = setTimeout(() => loadAbsences(abortController.signal), searchOptions.query.length === 0 ? 0 : 1000));

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [searchOptions.query, searchOptions.exactMatch, year, month]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    loadAbsences(abortController.signal);

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [
    // absencesPeriodQuickFilter.absencesPeriod,
    state.page,
    absencesStatuses,
    state.sort,
    year,
    month,
  ]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    if (state.employee?.id) {
      if (employeeGroupOption?.employeeGroupIds?.length || legalEntityOption?.legalEntityIds?.length) {
        let isInEEGroup = true;
        let isInLE = true;

        if (employeeGroupOption?.employeeGroupIds?.length) {
          const employeeEEGroupIds = state.employee.groups?.map((group) => group.id) ?? [];

          isInEEGroup = !!employeeEEGroupIds.find((id) => employeeGroupOption?.employeeGroupIds.includes(id));
        }

        if (legalEntityOption?.legalEntityIds?.length) {
          isInLE = !!legalEntityOption?.legalEntityIds.includes(state.employee!.legalEntity!.id);
        }

        if (!(isInEEGroup && isInLE)) {
          setState((state) => ({
            ...state,
            allEmployees: true,
            employee: null,
            tnAEmployeeActualConfig: null,
            items: [],
            totalItems: 0,
            // counters: [],
            employeeQfId: uuidv4(),
          }));
        } else {
          loadAbsences(abortController.signal);
        }
      } else {
        loadAbsences(abortController.signal);
      }
    } else {
      setState((state) => ({
        ...state,
        allEmployees: true,
        employee: null,
        tnAEmployeeActualConfig: null,
        items: [],
        totalItems: 0,
        // counters: [],
        employeeQfId: uuidv4(),
      }));

      loadAbsences(abortController.signal);
    }

    return () => {
      clearTimeout(timer);
      abortController.abort();
    };
  }, [employeeGroupOption?.employeeGroupIds, legalEntityOption?.legalEntityIds]);

  return (
    <>
      {hasAccess ? (
        <LoadingOverlay
          loading={state.loading}
          error={state.error}
        >
          <TableHeader
            searchOptions={searchOptions}
            tableTitle={undefined}
            handleChangeSearchOptions={handleChangeSearchOptions}
            quickFilters={
              <>
                <FilterByYearAndMonth
                  filterColor={"filter"}
                  year={year}
                  month={month}
                  handleChangeYearAndMonth={(effect, date) => {
                    handleChangeYearAndMonth(effect, date);
                  }}
                  disabled={state.loading}
                />

                {!!legalEntityQuickFilter && legalEntityQuickFilter}
                {!!employeeGroupQuickFilter && <Grid sx={{ minWidth: "200px" }}>{employeeGroupQuickFilter}</Grid>}

                {/* <AbsencesPeriodQuickfilter
                  quickFilters={absencesPeriodQuickFilter}
                  handleChangeQuickFilters={setAbsencesPeriodQuickFilter}
                /> */}

                <AbsencesStatusesQuickfilter
                  quickFilters={absencesStatusesQuickFilter}
                  handleChangeQuickFilters={setAbsencesStatusesQuickFilter}
                  isMultiSelect
                />

                <Grid
                  style={{
                    borderRadius: "12px",
                    backgroundColor: theme.palette.background.paper,
                    display: "flex",
                    gap: "10px",
                    padding: "0px 10px",
                    alignItems: "center",
                  }}
                >
                  <Grid>
                    <Typography color={theme.palette.secondary.light}>{"Employee"}</Typography>
                  </Grid>
                  <Divider
                    flexItem
                    orientation={"vertical"}
                  />
                  <Grid>
                    <AutocompleteDropdown
                      key={state.employeeQfId}
                      field="employee"
                      childField="legalEntity"
                      childFieldProp="name"
                      serviceApi={new EmployeesApiService()}
                      apiLoadItemsPath={undefined}
                      handleChange={(_, employee) =>
                        setState((state) => ({
                          ...state,
                          allEmployees: employee?.id === allAutocompliteOption,
                          employee: employee?.id === allAutocompliteOption ? null : employee,
                          tnAEmployeeActualConfig: null,
                          items: [],
                        }))
                      }
                      inputVariant="standard"
                      selectedValue={state.employee}
                      inputStyle={{
                        minWidth: "130px",
                        paddingRight: "40px",
                        margin: "5px 0px",
                        padding: "1px 0",
                        fontSize: theme.typography.body1.fontSize,
                      }}
                      withAllOption
                      allOptionAsDefault
                      sxProps={{ minWidth: "130px" }}
                      propsFilter={
                        {
                          employeeGroupIds: employeeGroupOption?.employeeGroupIds ?? [],
                          legalEntityIds: legalEntityOption?.legalEntityIds ?? [],
                        } as Partial<EmployeesFilter>
                      }
                    />
                  </Grid>
                </Grid>

                {/* <ExportAllEmployeeLeavesToExcel
                  absencesPeriodQuickFilter={absencesPeriodQuickFilter}
                  loadAllAbsences={loadAllAbsences}
                  disabled={state.loading || !state.items.length}
                  state={state}
                /> */}
              </>
            }
          />

          {state.employee?.id && !state.allEmployees ? (
            <>
              {state.tnAEmployeeActualConfig ? (
                <>
                  {state.items.length > 0 && !state.loading ? (
                    <AllEmployeeLeavesCalendar
                      year={year}
                      month={month}
                      notifyParent={notifyParent}
                      absences={state.items}
                      allEmployeesSelected={false}
                      allowedOnlyApproveReject={false}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  ) : (
                    !state.loading &&
                    !state.error &&
                    state.items.length === 0 && (
                      <Grid
                        sx={{
                          width: "100%",
                          height:
                            height -
                            (SPACING[9] * 2 + (document.getElementById("header")?.clientHeight ?? 0) + SPACING[7]),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="h5">No absences found for the selected employee</Typography>
                      </Grid>
                    )
                  )}
                </>
              ) : (
                <Grid
                  sx={{
                    width: "100%",
                    height:
                      height - (SPACING[9] * 2 + (document.getElementById("header")?.clientHeight ?? 0) + SPACING[7]),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {!state.loading && !state.error && (
                    <Typography variant="h5">TnA not configured for this employee</Typography>
                  )}
                </Grid>
              )}
            </>
          ) : state.allEmployees ? (
            <>
              {state.items.length > 0 && !state.loading ? (
                <AllEmployeeLeavesCalendar
                  year={year}
                  month={month}
                  notifyParent={notifyParent}
                  absences={state.items}
                  allEmployeesSelected={true}
                  allowedOnlyApproveReject={state.allEmployees}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              ) : (
                !state.loading &&
                !state.error &&
                state.items.length === 0 && (
                  <Grid
                    sx={{
                      width: "100%",
                      height:
                        height - (SPACING[9] * 2 + (document.getElementById("header")?.clientHeight ?? 0) + SPACING[7]),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h5">No absences found</Typography>
                  </Grid>
                )
              )}
            </>
          ) : (
            <Grid
              sx={{
                width: "100%",
                height: height - (SPACING[9] * 2 + (document.getElementById("header")?.clientHeight ?? 0) + SPACING[7]),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {!state.loading && !state.error && <Typography variant="h5">Please select employee</Typography>}
            </Grid>
          )}
        </LoadingOverlay>
      ) : (
        <NotAuthorizedPage />
      )}
    </>
  );
};

export default AllEmployeeLeavesCalendarWrapper;
